import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, Upload, Download, DollarSign, CheckSquare, Square, Trash2, Sparkles, ChevronDown } from "lucide-react";

export default function CarsAdmin() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [modal, setModal] = useState(null); // 'price' | 'seo' | 'status' | null
  const fileRef = useRef(null);

  const load = () => {
    setBusy(true);
    api.get("/cars?limit=200").then((r) => setRows(r.data)).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const l = q.toLowerCase();
    return rows.filter((r) => `${r.brand_name} ${r.model} ${r.slug}`.toLowerCase().includes(l));
  }, [rows, q]);

  const toggle = (slug) => {
    const next = new Set(selected);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.slug)));
  };

  const bulkStatus = async (status) => {
    if (!selected.size) return toast.error("Select at least one car");
    try {
      const { data } = await api.post("/admin/cars/bulk/status", { slugs: [...selected], status });
      toast.success(`Updated ${data.modified} cars → ${status}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };

  const bulkDelete = async () => {
    if (!selected.size) return toast.error("Select at least one car");
    if (!window.confirm(`Delete ${selected.size} car(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/admin/cars/bulk/delete", { slugs: [...selected] });
      toast.success(`Deleted ${data.deleted} cars`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const bulkPrice = async (payload) => {
    if (!selected.size) return toast.error("Select at least one car");
    try {
      const { data } = await api.post("/admin/cars/bulk/price", { slugs: [...selected], ...payload });
      toast.success(`Updated ${data.updated} car prices`);
      setModal(null); setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk price failed"); }
  };

  const bulkSEO = async (payload) => {
    if (!selected.size) return toast.error("Select at least one car");
    try {
      const { data } = await api.post("/admin/cars/bulk/seo", { slugs: [...selected], ...payload });
      toast.success(`Updated ${data.updated} SEO fields`);
      setModal(null); setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk SEO failed"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/cars/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "cars.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/cars/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.inserted} new, ${data.updated} updated, ${data.errors} errors`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const allChecked = filtered.length > 0 && selected.size === filtered.length;

  return (
    <div data-testid="admin-cars">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Cars</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage the whole car catalogue with bulk operations.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} data-testid="csv-import-input"/>
          <button data-testid="csv-import" onClick={() => fileRef.current?.click()} className="h-10 px-4 rounded-full border border-[#E5E7EB] bg-white text-sm inline-flex items-center gap-2 hover:bg-[#F3F6FA]"><Upload className="w-4 h-4"/> Import CSV</button>
          <button data-testid="csv-export" onClick={exportCsv} className="h-10 px-4 rounded-full border border-[#E5E7EB] bg-white text-sm inline-flex items-center gap-2 hover:bg-[#F3F6FA]"><Download className="w-4 h-4"/> Export CSV</button>
        </div>
      </div>

      {/* Bulk toolbar */}
      <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-white p-3 flex flex-wrap items-center gap-2" data-testid="bulk-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <input data-testid="cars-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-10 pl-9 pr-3 rounded-full border border-[#E5E7EB] bg-white text-sm outline-none focus:border-blue-400 w-full"/>
        </div>
        <div className="text-xs text-muted-foreground px-2">Selected: <span className="font-semibold text-[#1F2937]" data-testid="bulk-selected-count">{selected.size}</span></div>
        <button data-testid="bulk-publish" onClick={() => bulkStatus("on_sale")} disabled={!selected.size} className="h-10 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm inline-flex items-center gap-1 disabled:opacity-50 hover:bg-[#F3F6FA]">Publish</button>
        <button data-testid="bulk-unpublish" onClick={() => bulkStatus("draft")} disabled={!selected.size} className="h-10 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm inline-flex items-center gap-1 disabled:opacity-50 hover:bg-[#F3F6FA]">Unpublish</button>
        <button data-testid="bulk-price" onClick={() => setModal("price")} disabled={!selected.size} className="h-10 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm inline-flex items-center gap-1 disabled:opacity-50 hover:bg-[#F3F6FA]"><DollarSign className="w-3.5 h-3.5"/> Price</button>
        <button data-testid="bulk-seo" onClick={() => setModal("seo")} disabled={!selected.size} className="h-10 px-3 rounded-full border border-[#E5E7EB] bg-white text-sm inline-flex items-center gap-1 disabled:opacity-50 hover:bg-[#F3F6FA]"><Sparkles className="w-3.5 h-3.5"/> SEO</button>
        <button data-testid="bulk-delete" onClick={bulkDelete} disabled={!selected.size} className="h-10 px-3 rounded-full border border-rose-200 text-rose-600 bg-white text-sm inline-flex items-center gap-1 disabled:opacity-50 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5"/> Delete</button>
      </div>

      {/* Table */}
      <div className="mt-4 rounded-3xl border border-[#E5E7EB] overflow-hidden bg-white">
        {busy ? (
          <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="p-3 w-12"><button data-testid="select-all" onClick={toggleAll}>{allChecked ? <CheckSquare className="w-4 h-4 text-[#2563EB]"/> : <Square className="w-4 h-4 text-muted-foreground"/>}</button></th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Model</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Body</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Fuel</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Price (ex)</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const on = selected.has(c.slug);
                return (
                  <tr key={c.slug} data-testid={`car-row-${c.slug}`} className={`border-t border-[#E5E7EB] ${on ? "bg-blue-50/40" : ""}`}>
                    <td className="p-3"><button data-testid={`select-${c.slug}`} onClick={() => toggle(c.slug)}>{on ? <CheckSquare className="w-4 h-4 text-[#2563EB]"/> : <Square className="w-4 h-4 text-muted-foreground"/>}</button></td>
                    <td className="p-3 font-medium">{c.brand_name} {c.model}<div className="text-xs text-muted-foreground">{c.slug}</div></td>
                    <td className="p-3 text-muted-foreground">{c.body_type}</td>
                    <td className="p-3 text-muted-foreground text-xs">{c.fuel_types?.join(", ")}</td>
                    <td className="p-3 font-mono">₹ {(c.price_min/100000).toFixed(1)}L</td>
                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${c.status === "on_sale" ? "bg-emerald-50 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>{c.status}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal === "price" && <PriceModal onClose={() => setModal(null)} onSave={bulkPrice} count={selected.size}/>}
      {modal === "seo" && <SEOModal onClose={() => setModal(null)} onSave={bulkSEO} count={selected.size}/>}
    </div>
  );
}

function PriceModal({ onClose, onSave, count }) {
  const [mode, setMode] = useState("delta_percent");
  const [val, setVal] = useState("");
  return (
    <Modal title={`Bulk price update — ${count} cars`} onClose={onClose} testId="modal-price">
      <div className="text-sm text-muted-foreground mb-4">Apply the same price change to all selected cars.</div>
      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === "delta_percent"} onChange={() => setMode("delta_percent")}/> Adjust by percentage (%)</label>
        <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === "delta_absolute"} onChange={() => setMode("delta_absolute")}/> Adjust by absolute amount (₹)</label>
        <input data-testid="price-value" value={val} onChange={(e) => setVal(e.target.value)} placeholder={mode === "delta_percent" ? "e.g. -5 for 5% off, 3 for 3% up" : "e.g. -10000 for ₹10,000 off"} className="w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm"/>
      </div>
      <div className="mt-6 flex gap-2 justify-end">
        <button onClick={onClose} className="h-10 px-4 rounded-full border border-[#E5E7EB] bg-white text-sm">Cancel</button>
        <button data-testid="price-apply" onClick={() => { const n = parseFloat(val); if (isNaN(n)) return toast.error("Enter a valid number"); onSave({ [mode]: n }); }} className="btn-primary h-10 px-4 text-sm">Apply</button>
      </div>
    </Modal>
  );
}

function SEOModal({ onClose, onSave, count }) {
  const [title, setTitle] = useState("{brand_name} {model} — Price, Specs, Mileage & Review");
  const [desc, setDesc] = useState("{brand_name} {model} in India — full specs, variants, EMI, insurance and honest verdict.");
  return (
    <Modal title={`Bulk SEO update — ${count} cars`} onClose={onClose} testId="modal-seo">
      <div className="text-sm text-muted-foreground mb-4">Placeholders: <code>{"{brand_name}"}</code>, <code>{"{model}"}</code>, <code>{"{slug}"}</code>, <code>{"{price_min}"}</code>, <code>{"{body_type}"}</code>.</div>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Meta title template</label>
      <input data-testid="seo-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm"/>
      <label className="block text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1 mt-3">Meta description template</label>
      <textarea data-testid="seo-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full min-h-[80px] rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm"/>
      <div className="mt-6 flex gap-2 justify-end">
        <button onClick={onClose} className="h-10 px-4 rounded-full border border-[#E5E7EB] bg-white text-sm">Cancel</button>
        <button data-testid="seo-apply" onClick={() => onSave({ meta_title_template: title, meta_description_template: desc })} className="btn-primary h-10 px-4 text-sm">Apply</button>
      </div>
    </Modal>
  );
}

function Modal({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl border border-[#E5E7EB]">
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}
